/**
 * Lecture des paramètres d'URL, sans schéma chargé d'emblée.
 *
 * Les filtres de chaque écran vivent dans l'URL, et `validateSearch` les lit
 * **avant** que la route ne charge : ce que ces lectures importent entre donc
 * dans le morceau d'entrée, que tout visiteur télécharge avant de voir quoi
 * que ce soit. Mesuré sur le morceau servi, `zod` y pesait 89,8 ko sur 482,
 * soit 19 % — pour une poignée de chaînes, d'énumérations et d'entiers (#210).
 *
 * Chaque lecture rend la valeur ou `undefined` : **un paramètre invalide ne
 * fait jamais échouer une route**, il disparaît. C'est ce que faisait
 * `.optional().catch(undefined)`, et `src/lib/search-params.test.ts` rejoue
 * 1 689 cas observés sur les schémas d'avant pour le prouver cas par cas.
 *
 * Le routeur a déjà analysé la valeur en JSON quand elle arrive ici :
 * `?page=2` vaut le nombre `2`, `?page=1e3` vaut `1000`, `?page=12abc` vaut la
 * chaîne « 12abc », et `?q=2` vaut le nombre `2` — d'où une lecture de texte
 * qui refuse un nombre.
 */

/** Une lecture : la valeur reconnue, ou `undefined`. */
export type Read<T> = (value: unknown) => T | undefined

/** Une chaîne, vide comprise ; tout le reste disparaît. */
export function text(): Read<string> {
  return (value) => (typeof value === 'string' ? value : undefined)
}

/** Une chaîne qui suit une forme, comme une date `2026-09-21`. */
export function matching(pattern: RegExp): Read<string> {
  return (value) => (typeof value === 'string' && pattern.test(value) ? value : undefined)
}

/** L'une des valeurs permises, et rien d'autre. */
export function oneOf<T extends string>(values: readonly T[]): Read<T> {
  return (value) =>
    typeof value === 'string' && (values as readonly string[]).includes(value)
      ? (value as T)
      : undefined
}

/** Une seule valeur acceptée — `demandes=true` et rien d'autre. */
export function exactly<T extends string | number | boolean>(expected: T): Read<T> {
  return (value) => (value === expected ? expected : undefined)
}

/** Un entier, au-dessus d'un plancher. Ni décimal, ni chaîne de chiffres. */
export function integer({ min }: { min?: number } = {}): Read<number> {
  return (value) =>
    typeof value === 'number' && Number.isInteger(value) && (min === undefined || value >= min)
      ? value
      : undefined
}

/** Un booléen, tel que le routeur l'a analysé — « true » la chaîne ne l'est pas. */
export function flag(): Read<boolean> {
  return (value) => (typeof value === 'boolean' ? value : undefined)
}

/**
 * Une liste séparée par des virgules, réduite aux valeurs connues.
 *
 * Ce qui n'est pas reconnu tombe, les trous se referment, et une liste qui ne
 * garde rien vaut `undefined` — un filtre vide n'est pas un filtre. **Les
 * doublons sont gardés** : c'est ce que faisait la lecture d'avant, et le
 * serveur s'en accommode.
 */
export function csvOf(allowed: Iterable<string>): Read<string> {
  const known = new Set(allowed)
  return (value) => {
    if (typeof value !== 'string') return undefined
    const kept = value.split(',').filter((part) => known.has(part))
    return kept.length > 0 ? kept.join(',') : undefined
  }
}

/** Une lecture qui ne rend jamais `undefined` : elle retombe sur sa valeur. */
export type Total<T> = ((value: unknown) => T) & { readonly total: true }

export function orElse<T>(read: Read<T>, fallback: T): Total<T> {
  const total = (value: unknown): T => read(value) ?? fallback
  return Object.assign(total, { total: true as const })
}

/** Une forme : un nom de paramètre, une lecture. */
export type Shape = Record<string, ((value: unknown) => unknown) & { total?: true }>

type Out<S extends Shape> = {
  [K in keyof S as S[K] extends { total: true } ? K : never]: ReturnType<S[K]>
} & {
  [K in keyof S as S[K] extends { total: true } ? never : K]?: ReturnType<S[K]>
}

/**
 * Lit tous les paramètres d'une forme.
 *
 * **Trois états, et non deux** — mesuré sur les schémas d'avant, et l'URL
 * sérialisée ensuite en dépend : un paramètre **absent** de l'URL est absent du
 * résultat ; un paramètre **présent mais refusé** garde sa clé et vaut
 * `undefined` ; une lecture à repli donne toujours sa valeur. Une clé posée à
 * `undefined` là où il n'y en avait pas réécrirait l'URL à la première
 * navigation.
 */
export function readSearch<S extends Shape>(shape: S, search: Record<string, unknown>): Out<S> {
  const out: Record<string, unknown> = {}
  for (const [key, read] of Object.entries(shape)) {
    if (read.total === true) out[key] = read(search[key])
    else if (key in search) out[key] = read(search[key])
  }
  return out as Out<S>
}
